import React, { Fragment, useCallback, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
} from "@material-ui/core";
import { mergeDeepRight, not, remove } from "ramda";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import { isBlank, mapIndexed, notBlank } from "../../util/ext";
import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import { makeStyles } from "@material-ui/core/styles";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import EditIcon from "@material-ui/icons/Edit";
import Grid from "@material-ui/core/Grid";
import LabeledReadOnly from "../../components/LabeledReadOnly";
import ReadOnlyField from "../../components/ReadOnlyField";
import YesNoCard from "../../components/YesNoCard";
import SubProjectsDialog from "../edit/SubProjectsDialog";
import ConfirmButton from "../../components/ConfirmButton";

const useStyles = makeStyles(theme => ({
  header: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },

  content: {
    paddingTop: 0,
  },
}));

const SubProjectsCard = ({ project, loading, onUpdate }) => {
  const classes = useStyles();

  const [dialogOpen, setDialogOpen] = useState(false);
  const handleAddClick = useCallback(() => {
    setDialogOpen(true);
  }, []);

  const handleCancel = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const handleDialogApply = useCallback(
    async data => {
      //call parent callback to save updated data.
      setDialogOpen(false);
      console.log(data);
      const update = mergeDeepRight(project, {
        fieldOptions: {
          sub_projects: {
            fieldLabel: data.subProjectsLabel,
            required: data.subProjectsRequired,
          },
        },
        subProjects: data.subProjects,
      });
      console.log(update);
      onUpdate(update);
    },
    [project, onUpdate],
  );

  const handleDeleteClick = useCallback(
    async idx => {
      const update = mergeDeepRight(project, {
        subProjects: remove(idx, 1, project.subProjects),
      });

      console.log(update);
      onUpdate(update);
    },
    [project, onUpdate],
  );

  return (
    <Card raised elevation={4} style={{ height: "100%" }}>
      <CardHeader
        titleTypographyProps={{
          color: "primary",
          variant: "h6",
        }}
        title="Sub Projects"
        action={
          <Tooltip title="Edit Sub Projects">
            <IconButton onClick={handleAddClick}>
              <EditIcon color="primary" fontSize="large" />
            </IconButton>
          </Tooltip>
        }
        classes={{ root: classes.header }}
      />
      <CardContent classes={{ root: classes.content }}>
        {loading && <PanelLoadingIndicator />}
        {isBlank(project) && (
          <Box padding={5} fontSize="1.4rem" fontStyle="italic">
            Error Loading Sub Projects.
          </Box>
        )}

        {notBlank(project) && (
          <Grid container spacing={3}>
            <Grid item sm={9}>
              <LabeledReadOnly label="Sub Project Label">
                <ReadOnlyField>
                  {project.fieldOptions["sub_projects"]
                    ? project.fieldOptions["sub_projects"].fieldLabel
                    : null}
                </ReadOnlyField>
              </LabeledReadOnly>
            </Grid>

            <Grid item sm={3}>
              <LabeledReadOnly label="Required">
                <YesNoCard
                  value={
                    project.fieldOptions["sub_projects"] &&
                    project.fieldOptions["sub_projects"].required
                  }
                />
              </LabeledReadOnly>
            </Grid>

            <Grid item sm={12}>
              <LabeledReadOnly label="Options">
                <Fragment>
                  {isBlank(project.subProjects) && (
                    <Box padding={5} fontSize="1.4rem" fontStyle="italic">
                      No Sub Projects are set up for this Project.
                    </Box>
                  )}
                  {notBlank(project.subProjects) && (
                    <List dense style={{ maxHeight: 150, overflowY: "auto" }}>
                      {mapIndexed(
                        (sub, idx) => (
                          <ListItem key={sub.subProjectId || sub.porojectName}>
                            <ListItemText
                              primary={sub.subProjectName}
                              secondary={not(sub.active) ? "INACTIVE" : null}
                              secondaryTypographyProps={{
                                color: "error",
                                variant: "subtitle2",
                              }}
                            />
                            <ListItemSecondaryAction>
                              {isBlank(sub.subProjectId) && (
                                <ConfirmButton
                                  handleDelete={() => handleDeleteClick(idx)}
                                  dialogTitle="Remove Sub Project"
                                  dialogBody="Are you sure you want to remove this sub project?"
                                />
                              )}
                            </ListItemSecondaryAction>
                          </ListItem>
                        ),
                        project.subProjects,
                      )}
                    </List>
                  )}
                </Fragment>
              </LabeledReadOnly>
            </Grid>
          </Grid>
        )}
      </CardContent>

      {dialogOpen && (
        <SubProjectsDialog
          open={dialogOpen}
          project={project}
          fieldOptions={project.fieldOptions}
          onCancel={handleCancel}
          onApply={handleDialogApply}
        />
      )}
    </Card>
  );
};

export default SubProjectsCard;
